import { useState, useEffect } from "react";
import {
	LOAN_PURPOSE_MORTGAGE_REFINANCE,
	LOAN_PURPOSE_MORTGAGE_INVESTMENT,
	LOAN_PURPOSE_MORTGAGE_OCCUPY,
	LOAN_PURPOSE_MORTGAGE_BUILD,
} from "@/lib/constants";
import { HandCoinsIcon, HardHatIcon, HouseLineIcon, SuitcaseIcon } from "@phosphor-icons/react";
import { useFormDataStore } from "@/components/context/useFormContext";
import { useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";

export const MortgageLoanDetails = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const { setFormError, enabledFields, formContainer, selectedIndex, setValidateFormDetails } =
		useFormStateStore();

	const { loanType, loanPurpose } = formValues;

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		loanPurpose: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			loanPurpose: enabledFields.loanPurpose?.length == 0 ? false : !loanPurpose,
		};

		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [loanPurpose]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		const fieldsToReset: (keyof typeof formValues)[] = ["loanPurpose"];

		fieldsToReset.forEach((field) => {
			if (enabledFields[field]?.length === 0) {
				setFormValue(field, ""); // Reset value when no options are available
				setErrors((prevErrors) => ({
					...prevErrors,
					field: false,
				}));
			}
		});
	}, [enabledFields, setFormValue, setErrors]);

	return (
		<div className="jF-space-y-4">
			{/* Loan Purpose */}
			{enabledFields.loanPurpose?.length > 1 && ( // Empty array means field is not supported.
				<RadioGroupField
					id="loanPurpose"
					label="Loan Purpose"
					value={loanPurpose || ""}
					options={enabledFields.loanPurpose?.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_PURPOSE_MORTGAGE_REFINANCE ? (
								<HandCoinsIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_MORTGAGE_INVESTMENT ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_MORTGAGE_OCCUPY ? (
								<HouseLineIcon size={24} weight="duotone" />
							) : item.value === LOAN_PURPOSE_MORTGAGE_BUILD ? (
								<HardHatIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						setFormValue("loanPurpose", value);
						setErrors((prevErrors) => ({
							...prevErrors,
							loanPurpose: !value, // Clear error when a selection is made
						}));
					}}
					required={true}
					error={errors.loanPurpose ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={2}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}
		</div>
	);
};
